import Lara from "@/presets/lara";

/**
 * Carousel pass-through for the curated-experiences carousels: bottom page indicator styled as a
 * scrollbar (track + thumb), prev/next as filled circles.
 *
 * `primaryButtonBgClass` sets the prev/next background so the buttons follow storefront branding
 * (pass `primaryColorClassBG` from useTenantTheming); defaults to the API primary. The buttons are
 * a filled circle with a white glyph, so the storefront color belongs on the circle, not the glyph.
 *
 * Deliberately a local copy rather than an import from the registration configurator's
 * `ExcursionIncludedSection/styles.ts`: the program page and the configurator are separate
 * surfaces owned by separate tickets, and sharing that module coupled this component to another
 * branch's refactors.
 */
export const curatedCarouselPreset = (primaryButtonBgClass = "bg-university-primary") => ({
  ...Lara.carousel,
  indicators: {
    class: ["flex flex-row justify-center items-center gap-1", "h-2 rounded-full", "p-0.5", "mt-4"],
  },
  indicator: {
    class: ["mr-2 mb-2"],
  },
  indicatorbutton: ({ context }: { context: { highlighted: boolean } }) => ({
    class: [
      "w-8 h-2 rounded-full transition-colors duration-200",
      "focus:outline-none focus:outline-offset-0 focus:ring focus:ring-gray-500/50",
      {
        "bg-gray-400 hover:bg-gray-500": !context.highlighted,
        "bg-gray-650 hover:bg-gray-700": context.highlighted,
      },
    ],
  }),
  itemscontent: {
    class: ["overflow-hidden w-full h-full z-10"],
  },
  content: {
    class: ["flex flex-col overflow-hidden"],
  },
  // Overrides Lara's own `container` purely to add `relative`: the prev/next buttons are
  // absolutely positioned and Lara's version is `flex` + orientation only, leaving them to
  // resolve against whatever ancestor happens to be positioned.
  container: ({ props }: { props: { orientation?: string } }) => ({
    class: [
      "relative flex",
      {
        "flex-row": props.orientation !== "vertical",
        "flex-col": props.orientation === "vertical",
      },
    ],
  }),
  previousbutton: {
    class: [
      // Vertically anchored to the image/text seam via the custom property the section
      // measures; `-translate-y-1/2` centres the circle on it.
      "absolute left-0 z-20 top-[var(--carousel-nav-top)] -translate-y-1/2",
      "overflow-hidden w-8 h-8",
      "rounded-full",
      `border-0 ${primaryButtonBgClass}`,
      "text-white",
      "transition duration-200 ease-in-out",
    ],
  },
  previousbuttonicon: {
    class: ["flex w-full justify-center items-center self-center"],
  },
  nextbutton: {
    class: [
      "absolute right-0 z-20 top-[var(--carousel-nav-top)] -translate-y-1/2",
      "overflow-hidden w-8 h-8",
      "rounded-full",
      `border-0 ${primaryButtonBgClass}`,
      "text-white",
      "transition duration-200 ease-in-out",
    ],
  },
  nextbuttonicon: {
    class: ["flex w-full justify-center items-center self-center"],
  },
});

/**
 * Dialog preset for the curated-experiences detail modal (AP-5593): keeps the
 * legacy EventsModal/SimplePopup look (square corners, tenant-dependent header
 * band, gray-75 body, centered footer) on top of PrimeVue Dialog.
 *
 * `headerColorClass` follows EventsModal: `bg-gray-275` for tenants with a
 * secondary branding color, `bg-university-accentPrimary` otherwise.
 */
export const experienceDetailModalPresets = (headerColorClass: string) => ({
  ...Lara,
  dialog: {
    ...Lara.dialog,
    root: {
      class: [
        // Shape & Shadow
        "rounded-none",
        "shadow-lg",
        "border-0",
        // Size
        "max-h-[90vh]",
        "w-full",
        "md:w-[800px]",
        "m-0",
      ],
    },
    header: {
      class: [
        // Flexbox & Alignment
        "flex items-center justify-between",
        "shrink-0",
        // Spacing
        "p-4",
        // Colors
        headerColorClass,
      ],
    },
    closeButton: {
      class: [
        "relative",
        // Flexbox and Alignment
        "flex items-center justify-center",
        // Size and Spacing
        "mr-2",
        "last:mr-0",
        "w-8 h-8",
        // Shape
        "border-0",
        "rounded-full",
        // Colors: dark X like the legacy SimplePopup close icon
        "text-black",
        "bg-transparent",
        // States
        "transition duration-200 ease-in-out",
        "hover:bg-surface-100",
        "focus:outline-none",
        // Misc
        "overflow-hidden",
      ],
    },
    content: {
      class: [
        // Spacing
        "px-8",
        "pt-6",
        "pb-2",
        // Colors & Text
        "bg-gray-75",
        "text-base",
        // Overflow handling
        "overflow-y-auto",
      ],
    },
    footer: {
      class: [
        // Flexbox & Alignment
        "flex",
        "items-center",
        "justify-center",
        "shrink-0",
        // Spacing
        "px-6",
        "pt-4",
        "pb-6",
        // Colors
        "bg-gray-75",
      ],
    },
  },
});
